import { useEffect } from "react";
import ahmad from "../../assets/ahmad_photo.webp";
import Socials from "../socials";
import { About } from "../aboutSection";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { FiMessageCircle } from "react-icons/fi";
import { TypeAnimation } from "react-type-animation";
import { HiArrowLongRight } from "react-icons/hi2";

const LandingScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const target = location?.pathname === "/about" ? "about" : "landing";
    document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
  }, [location]);

  return (
    <main>
      <section
        id="landing"
        aria-label="Introduction section"
        className="pt-36 sm:pb-36 sm:px-0 px-5 container flex justify-center sm:flex-row flex-col gap-10 sm:gap-20 items-center sm:items-start animate-fade-down animate-once animate-duration-400 animate-delay-0 animate-ease-in-out animate-normal animate-fill-both"
      >
        <figure className="relative">
          <div className="sm:w-[270px] w-[200px] sm:h-[270px] h-[200px] rounded-full" />
          <LazyLoadImage
            src={ahmad}
            alt="Ahmad Kaleem - Frontend Engineer specializing in React and Next.js"
            className="sm:w-[300px] sm:h-[300px] w-[230px] h-[230px] rounded-full -top-[30px] absolute"
            loading="lazy"
          />
        </figure>

        <article className="flex flex-col gap-y-4 max-w-[680px] justify-center sm:items-start items-center text-primary">
          <header>
            <h1 className="hollow-text text-[35px] sm:text-[50px] sm:text-start text-center font-[1000] tracking-[0.04em]">
              Frontend Engineer
            </h1>

            <h2 className="text-[var(--bg-btn)] text-[20px] sm:text-start text-center font-black tracking-[0.04em]">
              <TypeAnimation
                sequence={[
                  "Hi, I'm Ahmad K.—A Frontend Visionary!",
                  600,
                  "Hi, I'm Ahmad K.—A Problem Solver!",
                  600,
                  "Hi, I'm Ahmad K.—A Creative Thinker!",
                  600,
                ]}
                speed={50}
                repeat={Infinity}
              />
            </h2>
          </header>

          <p className="sm:text-start text-center">
            Frontend Engineer with a strong focus on{" "}
            <strong>building scalable, production-ready applications</strong>. I
            specialize in{" "}
            <strong>React, TypeScript, Next.js, and modern UI systems</strong>,
            with deep experience delivering complex platforms from scratch —
            including healthcare SaaS, admin dashboards, and real-time user
            portals.
          </p>

          <p className="sm:text-start text-center">
            As a <strong>sole frontend engineer</strong> on high-impact
            products, I've shipped secure role-based systems, real-time chat,
            analytics dashboards, payment integrations, and SEO-optimized
            landing pages used by <strong>thousands of active users</strong>. I
            care deeply about clean architecture, performance, and pixel-perfect
            UI helps turning complex business requirements into intuitive user
            experiences.
          </p>

          <blockquote className="relative inline-block text-center sm:text-left">
            <span className="absolute left-0 top-1/2 h-5 w-[2px] -translate-y-1/2 bg-[var(--bg-btn)] hidden lg:block rounded-full" />
            <p className="italic text-sm text-[var(--bg-btn)] px-3">
              Crafting reliable, high-quality frontends where product thinking
              meets clean code.
            </p>
          </blockquote>

          <nav aria-label="Primary actions">
            <Link
              to="/projects"
              className="text-secondary group italic tracking-wider flex justify-center items-center gap-1 font-light text-sm hover:opacity-80"
            >
              View Projects
              <HiArrowLongRight
                className="group-hover:translate-x-1 duration-150"
                size={15}
                aria-hidden="true"
              />
            </Link>
          </nav>

          <button
            onClick={() => navigate("/contact")}
            aria-label="Contact Ahmad to discuss a project"
            className="py-2 px-4 border-2 flex justify-center items-center gap-x-1 text-white rounded-full border-btn transition-all ease-in-out duration-200 hover:scale-[0.98] hover:opacity-85 bg-btn"
          >
            Let's Discuss
            <FiMessageCircle aria-hidden="true" />
          </button>

          <Socials />
        </article>
      </section>

      <section id="about" aria-label="About Ahmad">
        <About />
      </section>
    </main>
  );
};

export default LandingScreen;
